import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../api/client";

// Company is a plain string label across loans, baskets, assets and pledges — so
// renaming one onto an existing name is exactly what merging two companies means.
// The page makes that consequence visible before it is committed.
export default function CompanyManagementPage() {
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);   // the company name being renamed
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    apiClient.get("/companies")
      .then((res) => setCompanies(res.data || []))
      .catch(() => setError("טעינת רשימת החברות נכשלה"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (name) => {
    setEditing(name);
    setDraft(name);
    setError("");
    setResult(null);
  };

  const cancelEdit = () => { setEditing(null); setDraft(""); };

  const target = draft.trim();
  const isMerge = target !== "" && target !== editing && companies.includes(target);
  const unchanged = target === editing;

  const submit = async () => {
    if (!target || unchanged) return;
    if (isMerge && !window.confirm(
      `"${editing}" ימוזג לתוך "${target}".\n` +
      "כל ההלוואות, המסגרות, הנכסים והשעבודים המשויכים יעברו לחברה אחת. להמשיך?"
    )) return;

    setBusy(true);
    setError("");
    try {
      const res = await apiClient.put("/companies/rename", { from: editing, to: target });
      setResult(res.data);
      setCompanies(res.data.companies || []);
      setEditing(null);
      setDraft("");
    } catch (err) {
      setError(err.response?.data?.error || "שינוי שם החברה נכשל");
    } finally {
      setBusy(false);
    }
  };

  const total = result
    ? Object.values(result.updated).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="page">
      <header style={{ marginBottom: "var(--space-lg, 24px)" }}>
        <h1 style={{ margin: 0 }}>ניהול חברות</h1>
        <p style={{ color: "var(--text-secondary)", marginTop: 4 }}>
          שינוי שם חברה מעדכן את כל ההלוואות, המסגרות, הנכסים והשעבודים המשויכים אליה.
          שינוי לשם של חברה קיימת ממזג את השתיים לישות אחת.
        </p>
        {error && <p className="message-error">{error}</p>}
      </header>

      {result && (
        <div className="card" style={{ marginBottom: "var(--space-md, 16px)" }}>
          <strong>
            {result.merged
              ? `"${result.from}" מוזגה לתוך "${result.to}"`
              : `"${result.from}" שונתה ל־"${result.to}"`}
          </strong>
          <div style={{ color: "var(--text-secondary)", marginTop: 4 }}>
            {total === 0 ? "לא נמצאו רשומות משויכות." : (
              <>עודכנו: {result.updated.loans} הלוואות, {result.updated.baskets} מסגרות,{" "}
                {result.updated.assets} נכסים, {result.updated.pledges} שעבודים.</>
            )}
          </div>
        </div>
      )}

      <div className="card">
        {companies.length === 0 ? (
          <p style={{ color: "var(--text-secondary)", margin: 0 }}>אין חברות ברשימה.</p>
        ) : (
          <table className="table-minimal" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "right" }}>שם החברה</th>
                <th style={{ textAlign: "left", width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((name) => (
                <tr key={name}>
                  <td>
                    {editing === name ? (
                      <>
                        <input
                          className="input"
                          value={draft}
                          list="company-names"
                          autoFocus
                          disabled={busy}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          style={{ width: "100%", maxWidth: 360 }}
                        />
                        {isMerge && (
                          <div style={{ color: "var(--warning, #f59e0b)", fontSize: 12, marginTop: 4 }}>
                            ⚠ שם קיים — החברות ימוזגו לישות אחת
                          </div>
                        )}
                      </>
                    ) : name}
                  </td>
                  <td style={{ textAlign: "left" }}>
                    {editing === name ? (
                      <>
                        <button
                          className="btn btn-primary"
                          onClick={submit}
                          disabled={busy || !target || unchanged}
                        >
                          {isMerge ? "מזג" : "שמור"}
                        </button>{" "}
                        <button className="btn btn-ghost" onClick={cancelEdit} disabled={busy}>
                          ביטול
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-ghost" onClick={() => startEdit(name)} disabled={busy}>
                        שנה שם
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Lets the rename field autocomplete to an existing company — i.e. merge. */}
      <datalist id="company-names">
        {companies.map((c) => <option key={c} value={c} />)}
      </datalist>
    </div>
  );
}
